import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { formatIntegration, totalIntegration } from '../utils/integrationTime';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { openSettings } from '../utils/settingsIntent';
import { httpStatus } from '../utils/httpStatus';
import { masterBuildLabel, stackGroupPercent } from '../utils/stackProgress';
import { STACK_METHOD_QUERY_KEY, methodName, sameMethod } from '../utils/stackMethod';
import type {
  CalibrationMode,
  ExposureGroup,
  Image,
  LatestStackPreviewGroup,
  StackFrameDecision,
  StackGroupStatus,
  StackInputImage,
  StackFrameOrder,
  StackPreviewJob,
  StackMethod,
  StackScoringSettings,
  StackStretchPreview,
  StackColorRole,
} from '../api/types';
import StackPreviewInspector from './StackPreviewInspector';
import CalibrationMasterButton from './CalibrationMasterInspector';
import StackSnrCurve from './StackSnrCurve';
import ScoringChangedNote from './ScoringChangedNote';
import StackColorPreviewPanel from './StackColorPreviewPanel';
import {
  appliedProcessingQueryKey as channelProcessingQueryKey,
  colorCatalogQueryPrefix,
} from './channelProcessing';
import { colorSourceKey } from './stackColorSources';
import { defaultColorProcessing } from './stackColorProcessing';
import StackStretchControls from './StackStretchControls';
import StackColorCalibrationLine from './StackColorCalibrationLine';
import { calibrationOutdated } from './stackCalibrationStale';
import { isSkyOriented } from './stackOrientation';
import { carriedLabel, processingRunFor, runProgress } from './stackProcessingLabel';
import { useAccess } from '../auth/access';
import { STACK_ACTIVITY_QUERY_KEY, useStackActivity } from '../hooks/useStackActivity';
import { JOBS_QUERY_KEY } from '../api/jobs';
import {
  penaltyParamsOf,
  sameScoring,
  SCORING_DEFAULTS,
  useScoringPreferences,
  type ScoringPreferences,
} from '../hooks/useScoringPreferences';

type StackCandidateImage = Pick<
  Image,
  'id' | 'target_id' | 'target_name' | 'filter_name' | 'grading_status' | 'exposure_group'
>;

interface StackPreviewPanelProps {
  dbId: string;
  projectId: number;
  images: StackCandidateImage[];
  /** `scope`: every frame in the project or target, as the Stacks view
   *  passes them. */
  selectionSource: 'selected' | 'visible' | 'scope';
  /** How wide a result card is, in pixels; as many fit to a row as the
   *  page allows. `null` fills the row: one card, or two on a wide screen. */
  cardSize?: number | null;
  /** More run options beside the build buttons, such as stacking in WBPP. */
  actions?: ReactNode;
  onOpenImage: (imageId: number) => void;
  /** The target chosen in the header. With one chosen, stacks remembered
   *  from the project's other targets (a mosaic's other panels) stay out. */
  targetId?: number | null;
}

/** Every color role, so the server can narrow the person's default color
 *  pipeline to whichever composite a target's channels make. */
const ALL_COLOR_ROLES: StackColorRole[] = ['luminance', 'red', 'green', 'blue', 'ha', 'oiii', 'sii'];

/** A result card's width when the page does not choose one. */
export const DEFAULT_STACK_CARD_SIZE = 550;

interface ChannelInput {
  key: string;
  targetId: number;
  targetName: string;
  filterName: string;
  exposureGroup?: ExposureGroup | null;
  images: StackCandidateImage[];
}

interface StackArtifact {
  jobId: string;
  artifactRevision: string;
  acceptedOnly: boolean;
  order: StackFrameOrder;
  scoring: StackScoringSettings;
  /** Absent only from records older than the method. */
  method?: StackMethod;
  group: StackGroupStatus;
}

/** The card preview's cache key: the stack's revision, and the preview's
 *  own once it has been rendered again from the stack's FITS. */
function previewRevision(artifact: StackArtifact): string {
  const rendered = artifact.group.preview_revision;
  return rendered ? `${artifact.artifactRevision}.${rendered}` : artifact.artifactRevision;
}

function stackScoringSettings(preferences: ScoringPreferences): StackScoringSettings {
  return {
    penalty_satellite: preferences.satellite,
    penalty_pointing: preferences.pointing,
    penalty_temporal: preferences.temporal,
    penalty_hfr_rise: preferences.hfrRise,
    penalty_star_loss: preferences.starLoss,
    hfr_reject_above: preferences.hfrRejectAbove,
    star_count_reject_below: preferences.starCountRejectBelow,
  };
}

/** The inverse of `stackScoringSettings`, so an artifact's recorded policy
 * can be compared with the shared `sameScoring`. */
function scoringPreferencesOf(settings: StackScoringSettings): ScoringPreferences {
  return {
    satellite: settings.penalty_satellite,
    pointing: settings.penalty_pointing,
    temporal: settings.penalty_temporal,
    hfrRise: settings.penalty_hfr_rise ?? 1,
    starLoss: settings.penalty_star_loss ?? 1,
    hfrRejectAbove: settings.hfr_reject_above,
    starCountRejectBelow: settings.star_count_reject_below,
  };
}

const CALIBRATED_SCORING = stackScoringSettings(SCORING_DEFAULTS);

/** Artifacts created before scoring metadata used the calibrated policy. */
function builtScoringSettings(
  settings: StackScoringSettings | undefined
): StackScoringSettings {
  return settings ?? CALIBRATED_SCORING;
}

/**
 * Remembered like the accepted-only choice is per session, but across
 * reloads: how a stack calibrates is a lasting decision about someone's
 * calibration library, not about one build.
 */
const CALIBRATION_MODE_KEY = 'psf-guard.stack-calibration-mode';

function readCalibrationMode(): CalibrationMode {
  try {
    const stored = window.localStorage.getItem(CALIBRATION_MODE_KEY);
    return stored === 'on' || stored === 'off' ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

/**
 * Whether a build weighed its frames: any admitted frame carries a weight.
 * Read from the frames rather than the method, so a weighted stack from
 * before the method existed still shows them.
 */
function hasFrameWeights(artifact: StackArtifact): boolean {
  return artifact.group.frames.some((frame) => (frame.integration_weight?.length ?? 0) > 0);
}

/** A frame's weight in the stack mean, one value per channel. */
function frameWeightSummary(frame: StackFrameDecision): string {
  const weights = frame.integration_weight;
  if (!weights || weights.length === 0) return '—';
  return weights.map((weight) => weight.toFixed(2)).join(' / ');
}

/**
 * Whether the cards show their signal-to-noise chart expanded. Remembered
 * across reloads, like the calibration mode: someone who folds it away should
 * not have to fold it again every session, or once per card.
 *
 * Folded is not hidden. The heading and the verdict stay on the card, so the
 * answer is still there in one line — the chart never disappears because a
 * build is short or a value looks uninteresting, which is the sort of thing
 * that reads as a bug.
 */
const SNR_CURVE_KEY = 'psf-guard.stack-snr-curve';

function readSnrCurveOpen(): boolean {
  try {
    return window.localStorage.getItem(SNR_CURVE_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** The mode a finished artifact was built under; older artifacts ran auto. */
function builtCalibrationMode(group: StackGroupStatus): CalibrationMode {
  return group.calibration?.mode ?? 'auto';
}

/**
 * Per-channel exceptions to the project-wide calibration mode, remembered
 * like the mode itself. Keys carry the database and project, so target ids
 * from different catalogs cannot collide.
 */
const CALIBRATION_OVERRIDES_KEY = 'psf-guard.stack-calibration-overrides';

function overrideStorageKey(dbId: string, projectId: number, channel: string) {
  return `${dbId}:${projectId}:${channel}`;
}

function readCalibrationOverrides(): Record<string, CalibrationMode> {
  try {
    const raw = window.localStorage.getItem(CALIBRATION_OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const overrides: Record<string, CalibrationMode> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (value === 'auto' || value === 'on' || value === 'off') {
        overrides[key] = value;
      }
    }
    return overrides;
  } catch {
    return {};
  }
}

// `replaced`: a build that was being prepared became the builds it names,
// which are followed instead.
const terminalStates = new Set(['completed', 'failed', 'cancelled', 'replaced']);

function stackJobQueryKey(dbId: string, projectId: number, jobId: string | null) {
  return ['db', dbId, 'project', projectId, 'stack-preview', jobId] as const;
}

function latestStackQueryKey(dbId: string, projectId: number) {
  return ['db', dbId, 'project', projectId, 'stack-preview', 'latest'] as const;
}

function channelKey(targetId: number, filterName: string | null, exposure?: ExposureGroup | null) {
  return exposure
    ? JSON.stringify([targetId, filterName ?? '', exposure.key])
    : `${targetId}:${filterName ?? ''}`;
}

function channelLabel(filterName: string | null, exposure?: ExposureGroup | null) {
  return [filterName || 'No filter', exposure?.label].filter(Boolean).join(' · ');
}

function artifactStretchKey(artifact: StackArtifact) {
  return `${artifact.jobId}:${artifact.group.index}:${artifact.artifactRevision}`;
}

function appliedProcessingQueryKey(dbId: string, projectId: number, artifact: StackArtifact) {
  return channelProcessingQueryKey(
    dbId, projectId, artifact.jobId, artifact.group.index, artifact.artifactRevision
  );
}

const formatExposure = formatIntegration;

function registrationSummary(frame: StackFrameDecision): string {
  if (frame.disposition === 'reference') return 'Reference frame';
  if (frame.registration_rms_pixels == null) return '—';
  const matches = frame.matched_stars == null ? '' : ` · ${frame.matched_stars} stars`;
  return `${frame.registration_rms_pixels.toFixed(2)} px RMS${matches}`;
}

function staleReason(
  current: ChannelInput | undefined,
  inputImages: StackInputImage[],
  builtAcceptedOnly: boolean,
  acceptedOnly: boolean,
  builtCalibration: CalibrationMode,
  calibrationMode: CalibrationMode,
  builtOrder: StackFrameOrder,
  frameOrder: StackFrameOrder,
  builtScoring: StackScoringSettings,
  scoring: StackScoringSettings,
  builtMethod: StackMethod | undefined,
  currentMethod: StackMethod | undefined
): string | null {
  if (!current) return 'Out of date — this channel is not in the current input';
  if (inputImages.length === 0) return 'Out of date — rebuild required';

  const currentGrades = new Map(current.images.map((image) => [image.id, image.grading_status]));
  const builtGrades = new Map(inputImages.map((image) => [image.image_id, image.grading_status]));
  if (
    currentGrades.size !== builtGrades.size ||
    [...currentGrades.keys()].some((imageId) => !builtGrades.has(imageId))
  ) {
    return 'Out of date — input images changed';
  }
  if ([...currentGrades].some(([imageId, grade]) => builtGrades.get(imageId) !== grade)) {
    return 'Out of date — image grades changed';
  }
  if (builtAcceptedOnly !== acceptedOnly) {
    return 'Out of date — Accepted only changed';
  }
  if (builtCalibration !== calibrationMode) {
    return 'Out of date — calibration mode changed';
  }
  if (builtOrder !== frameOrder) {
    return 'Out of date — frame order changed';
  }
  if (!sameScoring(scoringPreferencesOf(builtScoring), scoringPreferencesOf(scoring))) {
    return 'Out of date — scoring settings changed';
  }
  // A stack that records no method predates the choice and Seiza 0.19.
  if (!builtMethod) {
    return 'Out of date — built before the stacking method could be chosen';
  }
  // Until the server's method has loaded there is nothing to compare.
  if (currentMethod && !sameMethod(builtMethod, currentMethod)) {
    return 'Out of date — stacking method changed';
  }
  return null;
}
// `calibrationMode` above is the channel's EFFECTIVE mode — its override
// when one is set, else the project-wide choice.

function artifactFromLatest(latest: LatestStackPreviewGroup | undefined): StackArtifact | undefined {
  if (!latest) return undefined;
  return {
    jobId: latest.job_id,
    artifactRevision: latest.artifact_revision,
    acceptedOnly: latest.accepted_only,
    order: latest.order ?? 'capture',
    scoring: builtScoringSettings(latest.scoring),
    method: latest.method,
    group: latest.group,
  };
}

/**
 * Whether the stack panel starts collapsed. Like the thumbnail size, this is a
 * preference about how someone reads the grid rather than about any image, so
 * it survives reloads. Storage can be unavailable (private browsing); the
 * panel then simply starts expanded.
 */
const PANEL_COLLAPSED_KEY = 'psf-guard.stack-panel-collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(PANEL_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function StackPreviewPanel({
  dbId,
  projectId,
  images,
  selectionSource,
  cardSize = DEFAULT_STACK_CARD_SIZE,
  actions,
  onOpenImage,
  targetId = null,
}: StackPreviewPanelProps) {
  const queryClient = useQueryClient();
  const { canCompute } = useAccess();
  const scoringPreferences = useScoringPreferences();
  const currentScoring = useMemo(
    () => stackScoringSettings(scoringPreferences),
    [scoringPreferences]
  );
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = () => setCollapsed((current) => {
    const next = !current;
    try {
      window.localStorage.setItem(PANEL_COLLAPSED_KEY, String(next));
    } catch {
      // Keep the in-memory preference even when it cannot be persisted.
    }
    return next;
  });
  const [acceptedOnly, setAcceptedOnly] = useState(false);
  // Not remembered across reloads: capture order is the free reading every
  // build produces, and quality order is a deliberate one-off question about
  // culling that should never be the state someone comes back to.
  const [frameOrder, setFrameOrder] = useState<StackFrameOrder>('capture');
  const [snrCurveOpen, setSnrCurveOpen] = useState(readSnrCurveOpen);
  const chooseSnrCurveOpen = (open: boolean) => {
    setSnrCurveOpen(open);
    try {
      window.localStorage.setItem(SNR_CURVE_KEY, open ? 'on' : 'off');
    } catch {
      // Keep the in-memory preference even when it cannot be persisted.
    }
  };
  // The server's stacking method, set on the Stacking settings page. Builds
  // use it on the server; the panel reads it to mark stacks built otherwise.
  const stackMethod = useQuery({
    queryKey: STACK_METHOD_QUERY_KEY,
    queryFn: apiClient.getStackMethod,
  });
  const currentMethod = stackMethod.data?.method;
  const [calibrationMode, setCalibrationMode] = useState<CalibrationMode>(readCalibrationMode);
  // A phone folds the build options behind one button; wider, they sit in the row.
  const optionsId = useId();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const chooseCalibrationMode = (mode: CalibrationMode) => {
    setCalibrationMode(mode);
    try {
      window.localStorage.setItem(CALIBRATION_MODE_KEY, mode);
    } catch {
      // Keep the in-memory preference even when it cannot be persisted.
    }
  };
  const [overrides, setOverrides] = useState<Record<string, CalibrationMode>>(
    readCalibrationOverrides
  );
  // '' clears the channel back to the project-wide mode.
  const chooseChannelCalibration = (channel: string, mode: CalibrationMode | '') => {
    setOverrides((current) => {
      const next = { ...current };
      const key = overrideStorageKey(dbId, projectId, channel);
      if (mode === '') {
        delete next[key];
      } else {
        next[key] = mode;
      }
      try {
        window.localStorage.setItem(CALIBRATION_OVERRIDES_KEY, JSON.stringify(next));
      } catch {
        // Keep the in-memory preference even when it cannot be persisted.
      }
      return next;
    });
  };
  const channelOverride = (channel: string): CalibrationMode | undefined =>
    overrides[overrideStorageKey(dbId, projectId, channel)];
  const effectiveCalibration = (channel: string): CalibrationMode =>
    channelOverride(channel) ?? calibrationMode;
  const [watchedJobIds, setWatchedJobIds] = useState<string[]>([]);
  // The Build button each build being prepared came from, by build.
  const preparingOperations = useRef(new Map<string, string>());
  const [inspector, setInspector] = useState<StackArtifact | null>(null);
  // Which channels currently show the stars image instead of the starless
  // one, keyed like `stretches`. Only meaningful while star removal is
  // applied to that channel.
  const [starsView, setStarsView] = useState<Record<string, boolean>>({});

  const currentChannels = useMemo(() => {
    const channels = new Map<string, ChannelInput>();
    for (const image of images) {
      const key = channelKey(image.target_id, image.filter_name, image.exposure_group);
      const existing = channels.get(key);
      if (existing) {
        existing.images.push(image);
      } else {
        channels.set(key, {
          key,
          targetId: image.target_id,
          targetName: image.target_name,
          filterName: image.filter_name ?? '',
          exposureGroup: image.exposure_group,
          images: [image],
        });
      }
    }
    for (const channel of channels.values()) {
      channel.images.sort((left, right) => left.id - right.id);
    }
    return channels;
  }, [images]);

  const stableImageIds = useMemo(
    () => [...images].map((image) => image.id).sort((left, right) => left - right),
    [images]
  );

  const latest = useQuery({
    queryKey: latestStackQueryKey(dbId, projectId),
    queryFn: () => apiClient.getLatestStackPreviews(dbId, projectId),
  });

  const {
    mutate: startStack,
    isPending: startPending,
    error: startError,
    variables: startVariables,
    reset: resetStart,
  } = useMutation({
    // A whole-set build goes to the server as one request it splits into a
    // build per channel, so the queue lists each channel and color follows
    // a target's last one; a channel build is a single request.
    mutationFn: async (variables: {
      force: boolean;
      imageIds: number[];
      operationKey: string;
      perChannel: boolean;
      channel?: ChannelInput;
    }) => {
      const request = {
        image_ids: variables.imageIds,
        accepted_only: acceptedOnly,
        force: variables.force,
        order: frameOrder,
        // Frame exclusion keys off reject recommendations, so the stack
        // must score with the same shared preferences as every other
        // surface — a satellite penalty of 0 keeps trailed frames in.
        scoring: penaltyParamsOf(scoringPreferences),
        calibration: calibrationMode,
        calibration_overrides: [...currentChannels.values()]
          .filter((channel) => channelOverride(channel.key) !== undefined)
          .map((channel) => ({
            target_id: channel.targetId,
            filter_name: channel.filterName,
            exposure_group_key: channel.exposureGroup?.key,
            calibration: channelOverride(channel.key)!,
          })),
        color_defaults: defaultColorProcessing(ALL_COLOR_ROLES),
        channel: variables.channel && {
          target_id: variables.channel.targetId,
          filter_name: variables.channel.filterName,
          exposure_group_key: variables.channel.exposureGroup?.key,
        },
      };
      return variables.perChannel
        ? apiClient.startStackPreviewChannels(dbId, projectId, request)
        : [await apiClient.startStackPreviews(dbId, projectId, request)];
    },
    onSuccess: (jobs, variables) => {
      for (const job of jobs) {
        queryClient.setQueryData(stackJobQueryKey(dbId, projectId, job.job_id), job);
        if (job.state === 'preparing') preparingOperations.current.set(job.job_id, variables.operationKey);
      }
      setWatchedJobIds((current) => [
        ...current,
        ...jobs.map((job) => job.job_id).filter((jobId) => !current.includes(jobId)),
      ]);
      queryClient.invalidateQueries({ queryKey: STACK_ACTIVITY_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
      if (jobs.some((job) => terminalStates.has(job.state))) {
        queryClient.invalidateQueries({ queryKey: latestStackQueryKey(dbId, projectId) });
      }
    },
  });

  const {
    mutate: stopStack,
    isPending: stopPending,
    error: stopError,
    reset: resetStop,
  } = useMutation({
    mutationFn: (jobId: string) => apiClient.cancelStackPreviewJob(dbId, projectId, jobId),
    onSuccess: (job) => {
      queryClient.setQueryData(stackJobQueryKey(dbId, projectId, job.job_id), job);
    },
  });

  const statuses = useQueries({
    queries: watchedJobIds.map((jobId) => ({
      queryKey: stackJobQueryKey(dbId, projectId, jobId),
      queryFn: () => apiClient.getStackPreviewJob(dbId, projectId, jobId),
      refetchInterval: (query: { state: { data?: StackPreviewJob } }) =>
        query.state.data && !terminalStates.has(query.state.data.state) ? 1000 : false,
    })),
  });
  const watchedJobs = useMemo(
    () =>
      statuses
        .map((status) => status.data)
        .filter((job): job is StackPreviewJob => job !== undefined && job.state !== 'replaced')
        .sort((left, right) => left.created_unix_seconds - right.created_unix_seconds),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useQueries returns a new array each render; the join tracks content.
    [statuses.map((status) => status.dataUpdatedAt).join('|')]
  );
  // A build being prepared becomes the builds it was prepared into, which
  // may already be queued, running or finished: follow those instead.
  const replacements = statuses
    .map((status) => status.data)
    .filter((job): job is StackPreviewJob => job?.state === 'replaced');
  const replacementKey = replacements
    .map((job) => `${job.job_id}>${(job.replaced_by ?? []).join(',')}`)
    .join('|');
  useEffect(() => {
    if (replacements.length === 0) return;
    for (const job of replacements) {
      preparingOperations.current.delete(job.job_id);
      for (const jobId of job.replaced_by ?? []) {
        queryClient.invalidateQueries({ queryKey: stackJobQueryKey(dbId, projectId, jobId) });
      }
    }
    setWatchedJobIds((current) => {
      const next = current.filter((jobId) => !replacements.some((job) => job.job_id === jobId));
      for (const job of replacements) {
        for (const jobId of job.replaced_by ?? []) {
          if (!next.includes(jobId)) next.push(jobId);
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the key tracks which builds were replaced and by what.
  }, [replacementKey, dbId, projectId, queryClient]);
  // A build the server no longer keeps, such as a stand-in replaced long
  // ago, leaves the watch rather than showing as an error: the activity line
  // still brings back anything running.
  const goneKey = statuses
    .map((status, index) => (httpStatus(status.error) === 404 ? watchedJobIds[index] : ''))
    .filter(Boolean)
    .join('|');
  useEffect(() => {
    if (!goneKey) return;
    const gone = goneKey.split('|');
    setWatchedJobIds((current) => current.filter((jobId) => !gone.includes(jobId)));
  }, [goneKey]);
  // Which Build button each build still being prepared came from.
  const preparingKeys = new Set(
    watchedJobs
      .filter((job) => job.state === 'preparing')
      .map((job) => preparingOperations.current.get(job.job_id))
      .filter((key): key is string => key !== undefined)
  );
  const unfinishedJobs = watchedJobs.filter((job) => !terminalStates.has(job.state));
  const statusError = statuses.find((status) => status.error && httpStatus(status.error) !== 404)?.error;
  // The job a shared status line describes: running work first, then the
  // newest of whatever remains.
  const activeJob: StackPreviewJob | undefined =
    unfinishedJobs.find((job) => job.state === 'running') ??
    unfinishedJobs[unfinishedJobs.length - 1] ??
    watchedJobs[watchedJobs.length - 1];
  const settledCount = watchedJobs.filter((job) => terminalStates.has(job.state)).length;
  useEffect(() => {
    if (settledCount > 0) {
      queryClient.invalidateQueries({ queryKey: latestStackQueryKey(dbId, projectId) });
    }
  }, [settledCount, dbId, projectId, queryClient]);

  useEffect(() => {
    setWatchedJobIds([]);
    setInspector(null);
    setStarsView({});
    setFrameOrder('capture');
    resetStart();
    resetStop();
  }, [dbId, projectId, resetStart, resetStop]);

  // Builds started before this panel mounted — or before the last navigation
  // — keep running on the server. Re-attach to every one of them so a queue
  // built up elsewhere stays visible. This must follow the reset above so a
  // project change adopts the new project's jobs rather than the old ones.
  const { active } = useStackActivity();
  const adoptableIds = useMemo(
    () =>
      active
        .filter(
          (entry) =>
            entry.kind === 'mono' && entry.database_id === dbId && entry.project_id === projectId
        )
        .map((entry) => entry.job_id),
    [active, dbId, projectId]
  );
  useEffect(() => {
    if (adoptableIds.length === 0) return;
    // A finished build runs again to render its previews from its FITS:
    // the line lists it, so read it again and follow it to the end.
    for (const jobId of adoptableIds) {
      const cached = queryClient.getQueryData<StackPreviewJob>(stackJobQueryKey(dbId, projectId, jobId));
      if (cached && terminalStates.has(cached.state)) {
        queryClient.invalidateQueries({ queryKey: stackJobQueryKey(dbId, projectId, jobId) });
      }
    }
    setWatchedJobIds((current) => {
      const additions = adoptableIds.filter((jobId) => !current.includes(jobId));
      return additions.length === 0 ? current : [...current, ...additions];
    });
  }, [adoptableIds, dbId, projectId, queryClient]);

  // The project's remembered and running stacks cover every target; the
  // grid shows one when the header names one, and so does this panel.
  // No target, 0 or a malformed one all mean every target, as in the grid.
  const scopedTarget = targetId && Number.isFinite(targetId) ? targetId : null;
  const onTarget = useCallback((id: number) => scopedTarget === null || id === scopedTarget, [scopedTarget]);
  const latestByChannel = useMemo(
    () =>
      new Map(
        (latest.data?.groups ?? [])
          .filter((entry) => onTarget(entry.group.target_id))
          .map((entry) => [
            channelKey(entry.group.target_id, entry.group.filter_name, entry.group.exposure_group),
            entry,
          ])
      ),
    [latest.data, onTarget]
  );
  const activeByChannel = useMemo(() => {
    const merged = new Map<string, { job: StackPreviewJob; group: StackPreviewJob['groups'][number] }>();
    for (const job of watchedJobs) {
      for (const group of job.groups) {
        if (!onTarget(group.target_id)) continue;
        const key = channelKey(group.target_id, group.filter_name, group.exposure_group);
        const existing = merged.get(key);
        // A build still holding the channel outranks a settled one; among
        // equals the newer request wins, matching iteration order.
        const groupBusy = group.state === 'queued' || group.state === 'running';
        const existingBusy =
          existing?.group.state === 'queued' || existing?.group.state === 'running';
        if (!existing || groupBusy || !existingBusy) {
          merged.set(key, { job, group });
        }
      }
    }
    return merged;
  }, [watchedJobs, onTarget]);

  const displayKeys = useMemo(() => {
    const keys = new Set([...currentChannels.keys(), ...latestByChannel.keys(), ...activeByChannel.keys()]);
    return [...keys].sort((leftKey, rightKey) => {
      const leftCurrent = currentChannels.get(leftKey);
      const rightCurrent = currentChannels.get(rightKey);
      const leftRemembered = latestByChannel.get(leftKey)?.group;
      const rightRemembered = latestByChannel.get(rightKey)?.group;
      const leftTarget = leftCurrent?.targetName ?? leftRemembered?.target_name ?? '';
      const rightTarget = rightCurrent?.targetName ?? rightRemembered?.target_name ?? '';
      const byTarget = leftTarget.localeCompare(rightTarget);
      if (byTarget !== 0) return byTarget;
      const leftFilter = leftCurrent?.filterName ?? leftRemembered?.filter_name ?? '';
      const rightFilter = rightCurrent?.filterName ?? rightRemembered?.filter_name ?? '';
      return leftFilter.localeCompare(rightFilter);
    });
  }, [activeByChannel, currentChannels, latestByChannel]);

  const artifactsByChannel = useMemo(() => new Map(displayKeys.map((key) => {
    const entry = activeByChannel.get(key);
    const artifact: StackArtifact | undefined = entry?.group.state === 'ready' ? {
      jobId: entry.job.job_id,
      artifactRevision: entry.job.artifact_revision,
      acceptedOnly: entry.job.accepted_only,
      order: entry.job.order ?? 'capture',
      scoring: builtScoringSettings(entry.job.scoring),
      method: entry.job.method,
      group: entry.group,
    } : artifactFromLatest(latestByChannel.get(key));
    return [key, artifact] as const;
  })), [displayKeys, activeByChannel, latestByChannel]);
  const processingArtifacts = [...artifactsByChannel.values()].filter(
    (artifact): artifact is StackArtifact => artifact !== undefined
  );
  const processingQueries = useQueries({
    queries: processingArtifacts.map((artifact) => ({
      queryKey: appliedProcessingQueryKey(dbId, projectId, artifact),
      queryFn: ({ signal }: { signal: AbortSignal }) => apiClient.getAppliedStackStretch(
        dbId, artifact.jobId, artifact.group.index, artifact.artifactRevision, signal
      ),
      retry: false,
      // A refreshed stack shows its previous result while the server
      // processes it again: look again until its own lands.
      refetchInterval: (query: { state: { data?: StackStretchPreview | null } }) => {
        const reprocessing = query.state.data?.reprocessing;
        return reprocessing && reprocessing.state !== 'failed' ? 3000 : false;
      },
    })),
  });
  const processingByArtifact = new Map(processingArtifacts.map((artifact, index) =>
    [artifactStretchKey(artifact), processingQueries[index]] as const
  ));

  // A processing run a person asked for keeps going on the server when the
  // page closes. When one on a shown channel ends, read that channel's
  // processing again, as the request that started it would have.
  const processingRunChannels = useRef(new Map<string, { jobId: string; groupIndex: number }>());
  useEffect(() => {
    const seen = processingRunChannels.current;
    const runs = new Map<string, { jobId: string; groupIndex: number }>();
    for (const artifact of processingArtifacts) {
      const run = processingRunFor(active, dbId, artifact.jobId, artifact.group.index);
      if (run) runs.set(run.job_id, { jobId: artifact.jobId, groupIndex: artifact.group.index });
    }
    for (const [runId, channel] of seen) {
      if (runs.has(runId)) continue;
      // A color preview that follows the channel card is out of date now.
      queryClient.invalidateQueries({ queryKey: colorCatalogQueryPrefix(dbId, projectId) });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key[0] === 'db' && key[1] === dbId && key[2] === 'project' && key[3] === projectId
            && key[4] === 'stack-processing'
            && typeof key[5] === 'string'
            && key[5].startsWith(`${channel.jobId}:${channel.groupIndex}:`);
        },
      });
    }
    processingRunChannels.current = runs;
  }, [active, dbId, processingArtifacts, projectId, queryClient]);
  const stretches: Record<string, StackStretchPreview | undefined> = Object.fromEntries(
    [...processingByArtifact].map(([key, query]) => [key, query.data ?? undefined])
  );

  const running = startPending || unfinishedJobs.length > 0;
  const queuedBuilds = unfinishedJobs.length;
  const buildLabel = latest.data?.groups.length ? 'Build current set' : 'Build stacks';
  // What the folded options hold off their defaults, named on the button.
  const optionsChanged = [
    acceptedOnly ? 'Accepted only' : null,
    calibrationMode === 'on' ? 'Calibration forced' : calibrationMode === 'off' ? 'No calibration' : null,
    frameOrder === 'quality' ? 'Quality order' : null,
  ].filter((note): note is string => note !== null);
  const error = startError ?? stopError ?? statusError ?? latest.error;
  const sourceText =
    selectionSource === 'selected' ? 'selected' : selectionSource === 'scope' ? '' : 'visible';
  // A failed stop — "that build already finished" — belongs to the build the
  // user was stopping, not to the next one they start.
  const beginAll = (force: boolean) => {
    resetStop();
    startStack({ force, imageIds: stableImageIds, operationKey: 'all', perChannel: true });
  };
  const beginChannel = (channel: ChannelInput, force: boolean) => {
    resetStop();
    // Every image in scope, with the channel named: the rest only lend
    // their reference frames, so this matches what Build stacks queues.
    startStack({
      force,
      imageIds: stableImageIds,
      operationKey: channel.key,
      perChannel: false,
      channel,
    });
  };

  function channelStale(key: string): boolean {
    const activeEntry = activeByChannel.get(key);
    const latestEntry = latestByChannel.get(key);
    const artifact =
      activeEntry && activeEntry.group.state === 'ready'
        ? {
            acceptedOnly: activeEntry.job.accepted_only,
            order: activeEntry.job.order ?? 'capture',
            scoring: builtScoringSettings(activeEntry.job.scoring),
            method: activeEntry.job.method,
            group: activeEntry.group,
          }
        : latestEntry
          ? {
              acceptedOnly: latestEntry.accepted_only,
              order: latestEntry.order ?? 'capture',
              scoring: builtScoringSettings(latestEntry.scoring),
              method: latestEntry.method,
              group: latestEntry.group,
            }
          : undefined;
    return artifact
      ? staleReason(
          currentChannels.get(key),
          artifact.group.input_images,
          artifact.acceptedOnly,
          acceptedOnly,
          builtCalibrationMode(artifact.group),
          effectiveCalibration(key),
          artifact.order,
          frameOrder,
          artifact.scoring,
          currentScoring,
          artifact.method,
          currentMethod
        ) !== null || calibrationStale(key)
      : false;
  }

  // A color stack's calibration goes out of date with a new solve or Gaia
  // catalog: its build preview, or the processing it shows instead.
  function calibrationStale(key: string): boolean {
    const card = artifactsByChannel.get(key);
    return card ? calibrationOutdated(card.group, stretches[artifactStretchKey(card)]) !== null : false;
  }
  const staleCount = displayKeys.filter(channelStale).length;
  const outdatedSourceKeys = useMemo(() => {
    const sourceKeys = new Set<string>();
    for (const entry of latest.data?.groups ?? []) {
      const key = channelKey(entry.group.target_id, entry.group.filter_name, entry.group.exposure_group);
      if (
        staleReason(
          currentChannels.get(key),
          entry.group.input_images,
          entry.accepted_only,
          acceptedOnly,
          builtCalibrationMode(entry.group),
          effectiveCalibration(key),
          entry.order ?? 'capture',
          frameOrder,
          builtScoringSettings(entry.scoring),
          currentScoring,
          entry.method,
          currentMethod
        )
      ) {
        sourceKeys.add(colorSourceKey({
          job_id: entry.job_id,
          group_index: entry.group.index,
          artifact_revision: entry.artifact_revision,
        }));
      }
    }
    return sourceKeys;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- effectiveCalibration reads calibrationMode and overrides, listed below.
  }, [
    acceptedOnly,
    calibrationMode,
    overrides,
    currentChannels,
    frameOrder,
    currentScoring,
    currentMethod,
    latest.data,
  ]);
  const colorSourceRevision = useMemo(
    () => (latest.data?.groups ?? [])
      .map((entry) => `${entry.job_id}:${entry.group.index}:${entry.artifact_revision}`)
      .sort()
      .join('|'),
    [latest.data?.groups]
  );

  // Every remembered channel's integration added up: the number people
  // quote for a project.
  const projectIntegrationSeconds = totalIntegration(
    (latest.data?.groups ?? []).map((entry) => entry.group.total_exposure_seconds)
  );

  return (
    <>
      <section
        className={`stack-preview-panel${cardSize === null ? ' is-fit' : ''}`}
        aria-labelledby="stack-preview-title"
        data-collapsed={collapsed || undefined}
        style={cardSize === null ? undefined : { '--stack-card-size': `${cardSize}px` } as CSSProperties}
      >
        <div className="stack-preview-heading">
          <div>
            <div className="stack-preview-eyebrow">
              Project integration
              {!collapsed && projectIntegrationSeconds > 0 && (
                <span className="stack-preview-eyebrow-total">
                  {' '}· {formatIntegration(projectIntegrationSeconds)} integrated
                </span>
              )}
            </div>
            <h2 id="stack-preview-title">
              <button
                type="button"
                className="stack-preview-collapse"
                aria-expanded={!collapsed}
                onClick={toggleCollapsed}
              >
                <span
                  className={`selector-chevron ${collapsed ? '' : 'expanded'}`}
                  aria-hidden="true"
                >
                  ▶
                </span>
                Stacks
              </button>
              {collapsed && (
                <small className="stack-preview-collapsed-summary">
                  {running
                    ? 'building…'
                    : `${latest.data?.groups.length ?? 0} remembered channel${
                        (latest.data?.groups.length ?? 0) === 1 ? '' : 's'
                      }${projectIntegrationSeconds > 0
                        ? ` · ${formatIntegration(projectIntegrationSeconds)} integrated`
                        : ''}`}
                </small>
              )}
            </h2>
            {!collapsed && (
              <p>
                Register and integrate the {stableImageIds.length}{sourceText ? ` ${sourceText}` : ''} images by exact
                target and channel. Rejected and quality-regrade frames are left out automatically.
              </p>
            )}
          </div>
          {!collapsed && (
          <div className="stack-preview-actions">
            {/* A build records the scoring it picked frames with, and the
                automatic refresh reuses it: never use a view's own silently. */}
            <ScoringChangedNote build />
            <button
              type="button"
              className={`stack-preview-options-toggle${optionsChanged.length ? ' is-set' : ''}`}
              aria-expanded={optionsOpen}
              aria-controls={optionsId}
              onClick={() => setOptionsOpen((open) => !open)}
            >
              <SlidersHorizontal size={14} aria-hidden="true" />
              Options
              {optionsChanged.length > 0 && <small>{optionsChanged.join(' · ')}</small>}
            </button>
            <div id={optionsId} className="stack-preview-options">
              <label className="stack-preview-checkbox">
                <input
                  type="checkbox"
                  checked={acceptedOnly}
                  disabled={running}
                  onChange={(event) => setAcceptedOnly(event.target.checked)}
                />
                Accepted only
              </label>
              <span className="stack-preview-method">
                Method:{' '}
                <button
                  type="button"
                  className="link-button"
                  title="Change how stacks integrate their frames in Settings, Stacking"
                  onClick={() => openSettings('stacking')}
                >
                  {currentMethod ? methodName(currentMethod, stackMethod.data) : '…'}
                </button>
              </span>
              <label
                className="stack-preview-calibration-mode"
                title={
                  'How the next build calibrates its lights. Auto applies the safe masters and ' +
                  'holds back combinations that damage the result, such as a flat with no bias ' +
                  'or dark master. Force on applies every master that can be built anyway. ' +
                  'Off stacks the raw frames.'
                }
              >
                Calibration
                <select
                  value={calibrationMode}
                  disabled={running}
                  onChange={(event) => chooseCalibrationMode(event.target.value as CalibrationMode)}
                >
                  <option value="auto">Auto</option>
                  <option value="on">Force on</option>
                  <option value="off">Off</option>
                </select>
              </label>
              <label
                className="stack-preview-frame-order"
                title={
                  'The order the next build integrates its frames in, which decides what its ' +
                  'signal-to-noise curve answers. Capture sequence starts with the best ' +
                  'registration reference, then follows the remaining eligible frames ' +
                  'chronologically. Quality order puts the best-graded frames first and asks ' +
                  'which frames are worth keeping: the depth where the curve leaves the ideal ' +
                  'is where the weaker frames stop paying for themselves. ' +
                  'Quality order integrates every frame again, so it cannot resume.'
                }
              >
                Order
                <select
                  value={frameOrder}
                  disabled={running}
                  onChange={(event) => setFrameOrder(event.target.value as StackFrameOrder)}
                >
                  <option value="capture">Capture sequence</option>
                  <option value="quality">Quality</option>
                </select>
              </label>
            </div>
            <button
              className="stack-preview-build"
              type="button"
              disabled={!canCompute || startPending || preparingKeys.has('all') || stableImageIds.length < 2}
              title={
                canCompute
                  ? 'Queues one build per channel; a channel already built as it is comes back at once, and color follows each target’s last channel.'
                  : 'This account can view cached stacks but cannot build them.'
              }
              onClick={() => beginAll(false)}
            >
              {startPending && startVariables?.operationKey === 'all'
                ? 'Queueing…'
                : preparingKeys.has('all')
                  ? 'Preparing…'
                  : buildLabel}
            </button>
            {!!latest.data?.groups.length && !running && (
              <button
                className="stack-preview-rebuild"
                type="button"
                disabled={!canCompute || stableImageIds.length < 2}
                title={canCompute ? undefined : 'This account can view cached stacks but cannot rebuild them.'}
                onClick={() => beginAll(true)}
              >
                Rebuild current set
              </button>
            )}
            {unfinishedJobs.length > 0 && (
              <button
                className="stack-preview-stop"
                type="button"
                disabled={stopPending}
                title="Stop every queued and running build. Channels already finished keep their previews."
                onClick={() => unfinishedJobs.forEach((job) => stopStack(job.job_id))}
              >
                {stopPending
                  ? 'Stopping…'
                  : unfinishedJobs.length > 1
                    ? `Stop all (${unfinishedJobs.length})`
                    : 'Stop'}
              </button>
            )}
            {actions}
          </div>
          )}
        </div>

        {!collapsed && <>
        {stableImageIds.length < 2 && (
          <div className="stack-preview-message">At least two visible images are required.</div>
        )}
        {error && (
          <div className="stack-preview-message error" role="alert">
            {error instanceof Error ? error.message : 'Stack failed'}
          </div>
        )}
        {activeJob?.error && <div className="stack-preview-message error">{activeJob.error}</div>}

        <StackColorPreviewPanel
          dbId={dbId}
          projectId={projectId}
          sourceRevision={colorSourceRevision}
          channelBuildRunning={running}
          outdatedSourceKeys={outdatedSourceKeys}
          canCompute={canCompute}
          onOpenImage={onOpenImage}
          targetId={scopedTarget}
        />

        {displayKeys.length > 0 && (
          <div
            className="stack-preview-results"
            data-job-state={activeJob?.state ?? 'remembered'}
          >
            <div className="stack-preview-statusline">
              <span className={`stack-preview-state ${activeJob?.state ?? 'remembered'}`}>
                {activeJob?.state ?? 'remembered'}
              </span>
              <span>
                {displayKeys.length} target/channel group{displayKeys.length === 1 ? '' : 's'}
              </span>
              {queuedBuilds > 1 && (
                <span className="stack-preview-queue-depth">{queuedBuilds} builds in the queue</span>
              )}
              {staleCount > 0 && (
                <span className="stack-preview-outdated-count">{staleCount} out of date</span>
              )}
            </div>
            <div className="stack-preview-grid">
              {displayKeys.map((key) => {
                const current = currentChannels.get(key);
                const activeEntry = activeByChannel.get(key);
                const activeGroup = activeEntry?.group;
                const artifact = artifactsByChannel.get(key);
                const stretchKey = artifact ? artifactStretchKey(artifact) : null;
                const processingQuery = stretchKey ? processingByArtifact.get(stretchKey) : undefined;
                const appliedStretch = stretchKey ? stretches[stretchKey] : undefined;
                const group = artifact?.group ?? activeGroup;
                const targetName = current?.targetName ?? group?.target_name ?? 'Unknown target';
                const filterName = current?.filterName ?? group?.filter_name ?? '';
                const exposureGroup = current?.exposureGroup ?? group?.exposure_group;
                const outdated = artifact
                  ? (staleReason(
                      current,
                      artifact.group.input_images,
                      artifact.acceptedOnly,
                      acceptedOnly,
                      builtCalibrationMode(artifact.group),
                      effectiveCalibration(key),
                      artifact.order,
                      frameOrder,
                      artifact.scoring,
                      currentScoring,
                      artifact.method,
                      currentMethod
                    ) ?? calibrationOutdated(artifact.group, appliedStretch))
                  : null;
                const groupBusy =
                  activeGroup?.state === 'queued' || activeGroup?.state === 'running';
                const canBuildChannel = (current?.images.length ?? 0) >= 2;
                const progressGroup = activeGroup ?? artifact?.group;
                const liveCurve = groupBusy ? activeGroup?.snr : null;
                const artifactCurve = artifact?.group.snr;
                  const calibration = artifact ? artifact.group.calibration : progressGroup?.calibration;
                const progressState = progressGroup?.state ?? 'not-built';
                const processedFrames = progressGroup?.processed_frames ?? 0;
                const eligibleFrames =
                  progressGroup?.eligible_frames ?? current?.images.length ?? 0;
                // The final rejection pass reads every frame three more
                // times after the frame count is complete, so the bar
                // counts those reads too.
                const progressPercentage =
                  progressState === 'ready'
                    ? 100
                    : progressGroup
                      ? stackGroupPercent(
                          progressGroup,
                          activeGroup ? activeEntry?.job.method : artifact?.method
                        )
                      : 0;
                const progressLabel =
                  progressState === 'ready' && progressGroup?.phase === 'render-queued'
                    ? 'Waiting to render preview'
                    : progressState === 'ready' && progressGroup?.phase === 'rendering'
                      ? 'Rendering preview'
                    : progressState === 'queued'
                    ? artifact
                      ? 'Rebuild queued'
                      : 'Waiting for stacker'
                    : progressState === 'running'
                      ? progressGroup?.phase === 'calibration'
                        ? progressGroup.calibration_progress
                          ? `Building ${masterBuildLabel(progressGroup.calibration_progress)}`
                          : 'Building calibration masters'
                        : progressGroup?.phase?.startsWith('Rejecting transients:')
                          ? progressGroup.phase
                        : progressGroup?.phase === 'orienting'
                          ? 'Solving and orienting sky view'
                        : progressGroup?.phase === 'rendering'
                          ? 'Rendering preview'
                          : artifact
                            ? 'Rebuilding stack'
                            : 'Registering frames'
                      : progressState === 'ready'
                        ? 'Stack ready'
                        : progressState === 'skipped'
                          ? 'Stack skipped'
                          : progressState === 'cancelled'
                            ? 'Stack stopped'
                            : progressState === 'error'
                              ? 'Stack failed'
                              : 'Not built';
                // While a master builds, the bar counts that master's frame
                // reads, and so does what it reports to assistive tech.
                const masterBuild = progressState === 'running' && progressGroup?.phase === 'calibration'
                  ? progressGroup.calibration_progress ?? null
                  : null;
                const reusedFrames = progressGroup?.reused_frames ?? 0;
                const progressDetail = progressGroup
                  ? `${processedFrames}/${eligibleFrames} frames${
                      reusedFrames > 0 ? ` · ${reusedFrames} resumed` : ''
                    }`
                  : `${current?.images.length ?? 0} candidates`;

                return (
                  <article
                    className={`stack-preview-card ${outdated ? 'outdated' : ''}`}
                    data-outdated={outdated ? 'true' : 'false'}
                    data-exposure-group={exposureGroup?.key}
                    key={key}
                  >
                    <header>
                      <div className="stack-preview-card-title">
                        <h3>{targetName}</h3>
                        <span className="stack-preview-channel">{filterName || 'No filter'}</span>
                        {exposureGroup && <span className="stack-preview-exposure">{exposureGroup.label}</span>}
                      </div>
                      <div className="stack-preview-card-actions">
                        <span className={`stack-group-state ${activeGroup?.state ?? group?.state ?? 'not-built'}`}>
                          {activeGroup?.state ?? group?.state ?? 'not built'}
                        </span>
                        <label
                          className="stack-preview-channel-calibration"
                          title={
                            'Calibration for this channel only. "Project" follows the ' +
                            'panel-wide Calibration choice; the other options override it ' +
                            'for this target, filter, and exposure group, and are remembered.'
                          }
                        >
                          <select
                            aria-label={`${targetName} ${channelLabel(filterName, exposureGroup)} calibration`}
                            value={channelOverride(key) ?? ''}
                            disabled={running}
                            onChange={(event) =>
                              chooseChannelCalibration(
                                key,
                                event.target.value as CalibrationMode | ''
                              )
                            }
                          >
                            <option value="">Project ({calibrationMode})</option>
                            <option value="auto">Auto</option>
                            <option value="on">Force on</option>
                            <option value="off">Off</option>
                          </select>
                        </label>
                        {artifact && (
                          <button
                            className="stack-preview-card-action"
                            type="button"
                            aria-label="Inspect full size"
                            title="Inspect full size"
                            onClick={() => setInspector(artifact)}
                          >
                            Inspect
                          </button>
                        )}
                        {artifact && (
                          <a
                            className="stack-preview-card-action"
                            href={apiClient.getStackFitsUrl(
                              dbId,
                              artifact.jobId,
                              artifact.group.index,
                              artifact.artifactRevision
                            )}
                            download
                            aria-label="Download linear FITS"
                            title="Download linear FITS"
                          >
                            FITS
                          </a>
                        )}
                        {current && (
                          <button
                            className="stack-preview-card-action"
                            type="button"
                            disabled={!canCompute || groupBusy || startPending || preparingKeys.has(key) || !canBuildChannel}
                            aria-label={artifact ? 'Rebuild channel' : 'Build channel'}
                            title={!canCompute
                              ? 'This account can view cached stacks but cannot build them.'
                              : artifact ? 'Rebuild channel' : 'Build channel'}
                            onClick={() => beginChannel(current, Boolean(artifact))}
                          >
                            {preparingKeys.has(key) && !groupBusy
                              ? 'Preparing…'
                              : groupBusy || (startPending && startVariables?.operationKey === key)
                              ? 'Building…'
                              : artifact
                                ? 'Rebuild'
                                : 'Build'}
                          </button>
                        )}
                      </div>
                    </header>

                    {outdated && <div className="stack-preview-outdated">{outdated}</div>}
                    {progressGroup?.resume_note && (
                      <div className="stack-preview-resume-note">{progressGroup.resume_note}</div>
                    )}

                    {artifact && (
                      <div className="stack-preview-image">
                        <img
                          src={(stretchKey && starsView[stretchKey] && appliedStretch?.stars_preview_url)
                            || appliedStretch?.preview_url
                            || apiClient.getStackPreviewUrl(
                              dbId, artifact.jobId, artifact.group.index, previewRevision(artifact)
                            )}
                          alt={`${targetName} ${channelLabel(filterName, exposureGroup)} stack preview`}
                        />
                        {isSkyOriented(artifact.group.sky_orientation) && (
                          <span className="stack-preview-orientation">N ↑ · E ←</span>
                        )}
                        {stretchKey && appliedStretch?.stars_preview_url && (
                          <div className="stack-preview-stars-toggle" role="group" aria-label="Star removal view">
                            <button
                              type="button"
                              aria-pressed={!starsView[stretchKey]}
                              onClick={() => setStarsView((current) => ({ ...current, [stretchKey]: false }))}
                            >
                              Starless
                            </button>
                            <button
                              type="button"
                              aria-pressed={Boolean(starsView[stretchKey])}
                              onClick={() => setStarsView((current) => ({ ...current, [stretchKey]: true }))}
                            >
                              Stars
                            </button>
                            {appliedStretch.stars_fits_url && (
                              <a href={appliedStretch.stars_fits_url} download title="Download the stars linear FITS">
                                Stars FITS
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {artifact && artifact.group.output_channels === 3 && (() => {
                      // Applied processing says what it did; without any, the
                      // build's own preview does.
                      const outcome = appliedStretch
                        ? appliedStretch.color_calibration
                        : artifact.group.color_calibration;
                      return outcome
                        ? <StackColorCalibrationLine outcome={outcome} canInstall={canCompute} />
                        : null;
                    })()}
                    {artifact && appliedStretch?.reprocessing && (
                      <p
                        className="stack-preview-carried"
                        role={appliedStretch.reprocessing.state === 'failed' ? 'alert' : 'status'}
                        data-carried-state={appliedStretch.reprocessing.state}
                      >
                        {carriedLabel(
                          appliedStretch.reprocessing,
                          active.find((entry) => entry.job_id === appliedStretch.reprocessing?.run_id)
                        )}
                      </p>
                    )}
                    {artifact && stretchKey && (
                      <>
                      {processingQuery?.isPending && (
                        <div className="stack-stretch-stats" role="status">Loading saved processing...</div>
                      )}
                      {processingQuery?.error && (
                        <div className="stack-stretch-error" role="alert">
                          Saved processing could not be loaded: {processingQuery.error.message}
                          <button type="button" onClick={() => processingQuery.refetch()}>Retry</button>
                        </div>
                      )}
                      <StackStretchControls
                        key={stretchKey}
                        label={`${targetName} ${channelLabel(filterName, exposureGroup)}`}
                        channels={artifact.group.output_channels === 3 ? 3 : 1}
                        disabled={!canCompute || running || processingQuery?.isPending || !!processingQuery?.error}
                        applied={appliedStretch}
                        serverRun={(() => {
                          const run = processingRunFor(
                            active, dbId, artifact.jobId, artifact.group.index
                          );
                          return run ? runProgress(run) : null;
                        })()}
                        apply={(request, onProgress, signal) => {
                          let listed = false;
                          return apiClient.applyStackStretch(
                            dbId, artifact.jobId, artifact.group.index, request,
                            {
                              onProgress: (progress) => {
                                // From its first 202 the run is in the
                                // header's queue; show it there at once.
                                if (!listed) {
                                  listed = true;
                                  queryClient.invalidateQueries({ queryKey: STACK_ACTIVITY_QUERY_KEY });
                                  queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
                                }
                                onProgress?.(progress);
                              },
                              signal,
                              revision: artifact.artifactRevision,
                            }
                          ).finally(() => {
                            // The run has left the line, finished or not;
                            // the header stops showing it at once.
                            queryClient.invalidateQueries({ queryKey: STACK_ACTIVITY_QUERY_KEY });
                          });
                        }}
                        onApplied={async (preview) => {
                          const queryKey = appliedProcessingQueryKey(dbId, projectId, artifact);
                          await queryClient.cancelQueries({ queryKey });
                          queryClient.setQueryData(queryKey, preview);
                          // A color preview that follows this card shows
                          // it is out of date without waiting for a poll.
                          queryClient.invalidateQueries({
                            queryKey: colorCatalogQueryPrefix(dbId, projectId),
                          });
                        }}
                        onRevert={async () => {
                          await apiClient.clearStackStretch(
                            dbId, artifact.jobId, artifact.group.index, artifact.artifactRevision
                          );
                          const queryKey = appliedProcessingQueryKey(dbId, projectId, artifact);
                          await queryClient.cancelQueries({ queryKey });
                          queryClient.setQueryData(queryKey, null);
                          queryClient.invalidateQueries({
                            queryKey: colorCatalogQueryPrefix(dbId, projectId),
                          });
                          // The next star removal starts on the starless
                          // view, not wherever this one was left.
                          setStarsView((current) => {
                            const next = { ...current };
                            delete next[stretchKey];
                            return next;
                          });
                        }}
                      />
                      </>
                    )}
                    {!artifact && groupBusy && (
                      <div className="stack-preview-placeholder">
                        <span className="stack-preview-spinner" aria-hidden="true" />
                        {activeGroup?.state === 'queued'
                          ? 'Waiting for stacker'
                          : activeGroup?.phase === 'calibration'
                            ? activeGroup.calibration_progress
                              ? `Building ${masterBuildLabel(activeGroup.calibration_progress)}`
                              : 'Matching and building calibration masters'
                            : activeGroup?.phase?.startsWith('Rejecting transients:')
                              ? activeGroup.phase
                            : activeGroup?.phase === 'rendering'
                              ? 'Rendering preview'
                              : 'Registering frames'}
                      </div>
                    )}
                    {!artifact && !groupBusy && (
                      <div className={`stack-preview-placeholder ${activeGroup?.state === 'error' ? 'error' : ''}`}>
                        {activeGroup?.error ??
                          (activeGroup?.state === 'cancelled'
                            ? 'This channel was stopped before it finished. Build it again when you are ready.'
                            : canBuildChannel
                              ? 'No stack has been built for this channel yet.'
                              : 'At least two current images are required for this channel.')}
                      </div>
                    )}

                    <div
                      className={`stack-preview-progress ${progressState}`}
                      data-stack-state={progressState}
                      role="status"
                      aria-live="polite"
                    >
                      <div className="stack-preview-progress-copy">
                        <span>{progressLabel}</span>
                        <span>{progressDetail}</span>
                      </div>
                      <div
                        className="stack-preview-progress-track"
                        role="progressbar"
                        aria-label={`${targetName} ${channelLabel(filterName, exposureGroup)} stack progress`}
                        aria-valuemin={0}
                        aria-valuemax={masterBuild ? masterBuild.total : eligibleFrames}
                        aria-valuenow={masterBuild ? masterBuild.done : processedFrames}
                        aria-valuetext={masterBuild ? `Building ${masterBuildLabel(masterBuild)}` : undefined}
                      >
                        <span style={{ width: `${progressPercentage}%` }} />
                      </div>
                    </div>

                    {progressGroup && (
                      <div className="stack-preview-metrics">
                        <div><strong>{progressGroup.accepted_frames}</strong><span>integrated</span></div>
                        <div><strong>{progressGroup.rejected_frames}</strong><span>stack rejects</span></div>
                        <div><strong>{progressGroup.quality_excluded}</strong><span>quality excluded</span></div>
                        {(progressGroup.calibration_excluded ?? 0) > 0 && (
                          <div title="No matching flat, bias or dark, or flats past the age limit. Open a frame to see its calibration issues.">
                            <strong>{progressGroup.calibration_excluded}</strong><span>can't calibrate</span>
                          </div>
                        )}
                        <div><strong>{formatExposure(progressGroup.total_exposure_seconds)}</strong><span>exposure</span></div>
                      </div>
                    )}

                    {artifactCurve && (
                      <StackSnrCurve
                        curve={artifactCurve}
                        label={`${targetName} ${channelLabel(filterName, exposureGroup)} completed stack`}
                        open={snrCurveOpen}
                        onOpenChange={chooseSnrCurveOpen}
                      />
                    )}
                    {liveCurve && (
                      <section className="stack-snr-live" aria-label="Live build SNR progress">
                        <strong>{artifact ? 'Live rebuild progress' : 'Live build progress'}</strong>
                        <StackSnrCurve
                          curve={liveCurve}
                          label={`${targetName} ${channelLabel(filterName, exposureGroup)} live build`}
                          open={snrCurveOpen}
                          onOpenChange={chooseSnrCurveOpen}
                        />
                      </section>
                    )}

                    {calibration && calibration.state !== 'none' && (
                      <div className="stack-preview-calibration">
                        <strong>
                          {calibration.state === 'applied'
                            ? calibration.mode === 'on'
                              ? 'Calibration applied (forced on)'
                              : 'Calibration applied'
                            : calibration.state === 'off'
                              ? 'Calibration off'
                              : calibration.state === 'incomplete'
                                ? 'Calibration set incomplete'
                                : 'Matching calibration'}
                          {(calibration.sessions ?? 0) > 1 &&
                            ` · ${calibration.sessions} sessions`}
                        </strong>
                        {calibration.state !== 'off' && (
                          <span>
                            {calibration.bias_frames} bias · {calibration.dark_frames} dark ·{' '}
                            {calibration.dark_flat_frames} dark-flat · {calibration.flat_frames} flat
                          </span>
                        )}
                        {calibration.warning && <span>{calibration.warning}</span>}
                      </div>
                    )}

                    {artifact && (
                      <>
                        <div className="calibration-master-entry">
                          <CalibrationMasterButton
                            dbId={dbId}
                            source={{ kind: 'mono', jobId: artifact.jobId, groupIndex: artifact.group.index, artifactRevision: artifact.artifactRevision }}
                            title={artifact.group.target_name}
                            label={channelLabel(artifact.group.filter_name, artifact.group.exposure_group)}
                          />
                        </div>
                        <details className="stack-preview-details">
                          <summary>Frame decisions ({artifact.group.frames.length})</summary>
                          <div className="stack-frame-table-wrap">
                            <table>
                              <thead>
                                <tr>
                                  <th>Image</th>
                                  <th>Quality</th>
                                  <th>Decision</th>
                                  <th>Registration</th>
                                  {hasFrameWeights(artifact) && (
                                    <th title="Weight in the stack mean, per channel. The reference weighs 1.">
                                      Weight
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {artifact.group.frames.map((frame) => (
                                  <tr key={frame.image_id}>
                                    <td>#{frame.image_id}</td>
                                    <td>{frame.quality_score?.toFixed(2) ?? '—'}</td>
                                    <td title={frame.reason ?? undefined}>{frame.disposition}</td>
                                    <td>{frame.reason || registrationSummary(frame)}</td>
                                    {hasFrameWeights(artifact) && (
                                      <td
                                        className="stack-frame-weight"
                                        title={frame.noise_sigma?.length
                                          ? `Noise ${frame.noise_sigma.map((sigma) => sigma.toPrecision(3)).join(' / ')}`
                                          : undefined}
                                      >
                                        {frameWeightSummary(frame)}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      </>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        )}
        </>}
      </section>
      {inspector && (
        <StackPreviewInspector
          eyebrow="Full-resolution integration"
          title={inspector.group.target_name}
          label={channelLabel(inspector.group.filter_name, inspector.group.exposure_group)}
          summary={[
            `${inspector.group.accepted_frames} frames`,
            `${formatIntegration(inspector.group.total_exposure_seconds)} integrated`,
            ...(isSkyOriented(inspector.group.sky_orientation)
              ? ['North up · East left']
              : []),
          ]}
          imageUrl={stretches[artifactStretchKey(inspector)]?.original_preview_url ??
            apiClient.getStackPreviewUrl(
              dbId,
              inspector.jobId,
              inspector.group.index,
              previewRevision(inspector),
              'original'
            )}
          fitsUrl={stretches[artifactStretchKey(inspector)]?.fits_url ??
            apiClient.getStackFitsUrl(
              dbId,
              inspector.jobId,
              inspector.group.index,
              inspector.artifactRevision
            )}
          imageAlt={`Full-resolution stack for ${inspector.group.target_name} ${channelLabel(inspector.group.filter_name, inspector.group.exposure_group)}`}
          downloadLabel={stretches[artifactStretchKey(inspector)]?.fits_url
            ? 'Download deconvolved linear FITS'
            : 'Download linear FITS'}
          artifactSource={{
            kind: 'mono',
            dbId,
            jobId: inspector.jobId,
            groupIndex: inspector.group.index,
            artifactRevision: inspector.artifactRevision,
          }}
          artifactEnabled={canCompute}
          onOpenImage={onOpenImage}
          onClose={() => setInspector(null)}
        />
      )}
    </>
  );
}
